import axios from 'axios';
import { toast } from 'sonner';
import { useMemo, useState } from 'react';
import { Check, Plus, Tag as TagIcon } from 'lucide-react';

import { TagChip } from './TagChip';
import { Button } from '@/components/ui/button';
import { type TagDto, useCreateTag, useTags } from '../api/tags-api';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';

interface TagSelectorProps {
  /** Allow inline "Create tag" affordance. Default true. */
  allowCreate?: boolean;
  className?: string;
  emptyHint?: string;
  onChange: (tagIds: string[]) => void;
  placeholder?: string;
  value: string[];
}

/**
 * Multi-select tag picker. Used both for assigning tags to documents and for
 * picking tags to gate sharing scope. Reads the org's tag library and lets
 * users create new tags inline.
 */
export function TagSelector({
  value,
  onChange,
  className,
  allowCreate = true,
  placeholder = 'Select tags…',
  emptyHint = 'No tags yet — type a name and press Create.',
}: TagSelectorProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const { data: tags = [] } = useTags();
  const create = useCreateTag();

  const selectedTags = useMemo(() => tags.filter((t) => value.includes(t.id)), [tags, value]);
  const trimmedSearch = search.trim();
  const exactMatch = useMemo(
    () => tags.find((t) => t.name.toLowerCase() === trimmedSearch.toLowerCase()),
    [tags, trimmedSearch],
  );

  const toggle = (tag: TagDto) => {
    if (value.includes(tag.id)) {
      onChange(value.filter((id) => id !== tag.id));
    } else {
      onChange([...value, tag.id]);
    }
  };

  const handleCreate = async () => {
    if (!trimmedSearch || exactMatch) return;
    try {
      const created = await create.mutateAsync({ name: trimmedSearch });
      onChange([...value, created.id]);
      setSearch('');
    } catch (err) {
      toast.error(extractApiError(err, 'Failed to create tag'));
    }
  };

  return (
    <div className={className}>
      {/*
        Chips render *outside* the popover trigger so the chip's remove
        button isn't nested inside the trigger's <button> (the previous
        layout produced a button-in-button DOM nesting warning).
      */}
      <div className="flex min-h-9 w-full flex-wrap items-center gap-1 rounded-md border bg-background px-2 py-1.5">
        {selectedTags.length === 0 ? (
          <span className="text-muted-foreground text-sm">{placeholder}</span>
        ) : (
          <>
            <TagIcon aria-hidden className="h-3.5 w-3.5 text-muted-foreground" />
            {selectedTags.map((t) => (
              <TagChip key={t.id} tag={t} onRemove={() => toggle(t)} />
            ))}
          </>
        )}
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              size="sm"
              type="button"
              variant="ghost"
              className="ml-auto h-7 px-2 text-xs"
              aria-label={selectedTags.length === 0 ? placeholder : 'Edit selected tags'}>
              <Plus aria-hidden className="h-3.5 w-3.5" />
              {selectedTags.length === 0 ? 'Add' : 'Edit'}
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-72 p-0">
            <Command shouldFilter>
              <CommandInput value={search} placeholder="Search or create a tag…" onValueChange={setSearch} />
              <CommandList>
                <CommandEmpty>
                  <div className="py-3 text-sm text-muted-foreground text-center">
                    {trimmedSearch ? `No tag matches "${trimmedSearch}"` : emptyHint}
                  </div>
                </CommandEmpty>
                <CommandGroup>
                  {tags.map((tag) => {
                    const checked = value.includes(tag.id);
                    return (
                      <CommandItem key={tag.id} value={tag.name} onSelect={() => toggle(tag)}>
                        <div className="flex items-center gap-2 flex-1">
                          <div
                            aria-hidden
                            className="h-3 w-3 rounded-full border"
                            style={{ backgroundColor: tag.color ?? 'transparent' }}
                          />
                          <span>{tag.name}</span>
                        </div>
                        {checked && <Check className="h-4 w-4 ml-2" />}
                      </CommandItem>
                    );
                  })}
                  {allowCreate && trimmedSearch && !exactMatch && (
                    <CommandItem className="text-primary" value={`__create__${trimmedSearch}`} onSelect={handleCreate}>
                      <Plus className="h-3.5 w-3.5 mr-2" />
                      Create &quot;{trimmedSearch}&quot;
                    </CommandItem>
                  )}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}

function extractApiError(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    const detail = err.response?.data?.detail;
    if (typeof detail === 'string' && detail.length > 0) return detail;
  }
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}
